import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Upload, FileText, AlertTriangle, CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useAccountNicknames } from '../hooks/useAccountNicknames'
import { parseThinkorswimCSV, buildTransactionRow } from '../lib/csvParser'

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function fmtDate(isoStr) {
  if (!isoStr) return '—'
  const d = new Date(isoStr)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function fmtRelativeTime(isoStr) {
  if (!isoStr) return '—'
  const now = new Date()
  const then = new Date(isoStr)
  const diffMs = now - then
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`
  if (diffDay === 1) return 'Yesterday'
  if (diffDay < 7) return `${diffDay} days ago`
  return fmtDate(isoStr)
}

// ── Component ────────────────────────────────────────────────────────────────

export default function ImportCSV() {
  const { user } = useAuth()
  const { settings: nicknameSettings } = useAccountNicknames()

  // State
  const [file, setFile] = useState(null)
  const [parsed, setParsed] = useState(null)
  const [selectedAccount, setSelectedAccount] = useState('')
  const [importing, setImporting] = useState(false)
  const [progress, setProgress] = useState('')
  const [result, setResult] = useState(null)
  const [overlapWarning, setOverlapWarning] = useState(null)
  const [duplicateImportWarning, setDuplicateImportWarning] = useState(null)
  const [importHistory, setImportHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(true)
  const fileInputRef = useRef(null)

  // Build account options from nicknames
  const accountOptions = Object.entries(nicknameSettings).map(([acctNum, s]) => ({
    value: acctNum,
    label: s.nickname ? `${s.nickname} (${acctNum})` : acctNum,
    nickname: s.nickname || '',
  }))

  // ── Load import history ────────────────────────────────────────────────────
  const loadHistory = useCallback(async () => {
    if (!user) return
    setHistoryLoading(true)
    const { data } = await supabase
      .from('import_history')
      .select('*')
      .eq('user_id', user.id)
      .order('imported_at', { ascending: false })
      .limit(10)

    setImportHistory(data || [])
    setHistoryLoading(false)
  }, [user])

  useEffect(() => { loadHistory() }, [loadHistory])

  // ── Check for duplicate import (same filename + account) ──────────────────
  const checkDuplicateImport = useCallback(async (filename, accountNumber) => {
    if (!user || !filename || !accountNumber) {
      setDuplicateImportWarning(null)
      return
    }

    const { data } = await supabase
      .from('import_history')
      .select('imported_at')
      .eq('user_id', user.id)
      .eq('filename', filename)
      .eq('account_number', accountNumber)
      .order('imported_at', { ascending: false })
      .limit(1)

    if (data && data.length > 0) {
      const importDate = fmtDate(data[0].imported_at)
      setDuplicateImportWarning(`This file was already imported for this account on ${importDate}. Duplicates will be skipped, but re-importing is not recommended.`)
    } else {
      setDuplicateImportWarning(null)
    }
  }, [user])

  // ── File selection & parse ───────────────────────────────────────────────
  const handleFile = useCallback((f) => {
    if (!f || !f.name.endsWith('.csv')) return
    setFile(f)
    setParsed(null)
    setResult(null)
    setOverlapWarning(null)
    setDuplicateImportWarning(null)

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = parseThinkorswimCSV(e.target.result)
        setParsed(data)

        // Auto-select account if CSV name matches a nickname
        if (data.accountName) {
          const match = accountOptions.find(o =>
            o.nickname.toLowerCase() === data.accountName.toLowerCase()
          )
          if (match) setSelectedAccount(match.value)
        }
      } catch (err) {
        setResult({ type: 'error', message: `Parse error: ${err.message}` })
      }
    }
    reader.readAsText(f)
  }, [accountOptions])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    const f = e.dataTransfer?.files?.[0]
    if (f) handleFile(f)
  }, [handleFile])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  // ── Check for overlapping transactions ───────────────────────────────────
  const checkOverlap = useCallback(async () => {
    if (!parsed || !selectedAccount || !user) return
    const { summary } = parsed
    if (!summary.dateRange) return

    const { count } = await supabase
      .from('schwab_transactions')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('account_number', selectedAccount)
      .gte('trade_date', summary.dateRange.from)
      .lte('trade_date', summary.dateRange.to)

    if (count > 0) {
      setOverlapWarning(`${count} existing transactions found in this date range for this account. Duplicates will be automatically skipped.`)
    } else {
      setOverlapWarning(null)
    }
  }, [parsed, selectedAccount, user])

  // Run overlap check when account changes
  useState(() => {
    if (selectedAccount && parsed) checkOverlap()
  }, [selectedAccount, parsed])

  // ── Import ───────────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!parsed || !selectedAccount || !user) return
    setImporting(true)
    setResult(null)

    try {
      // Step 1: Build transaction rows
      setProgress('Preparing transactions...')
      const rows = parsed.executions.map(exec =>
        buildTransactionRow(exec, user.id, selectedAccount)
      )

      // Step 2: Check for existing activity_ids (deduplication)
      setProgress('Checking for duplicates...')
      const activityIds = rows.map(r => r.activity_id)

      // Batch check in chunks of 100
      const existingIds = new Set()
      for (let i = 0; i < activityIds.length; i += 100) {
        const chunk = activityIds.slice(i, i + 100)
        const { data: existing } = await supabase
          .from('schwab_transactions')
          .select('activity_id')
          .eq('user_id', user.id)
          .in('activity_id', chunk)

        if (existing) {
          existing.forEach(r => existingIds.add(r.activity_id))
        }
      }

      const newRows = rows.filter(r => !existingIds.has(r.activity_id))
      const skippedCount = rows.length - newRows.length

      if (newRows.length === 0) {
        setResult({
          type: 'success',
          message: `All ${rows.length} transactions already exist. Nothing to import.`,
        })
        // Save history even for all-duplicate imports
        await saveImportHistory(0, skippedCount, 0, 0)
        setFile(null)
        setParsed(null)
        setSelectedAccount('')
        setOverlapWarning(null)
        setDuplicateImportWarning(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
        setImporting(false)
        return
      }

      // Step 3: Insert in batches
      setProgress(`Inserting ${newRows.length} transactions...`)
      const BATCH_SIZE = 50
      let insertedCount = 0
      let insertErrors = []

      for (let i = 0; i < newRows.length; i += BATCH_SIZE) {
        const batch = newRows.slice(i, i + BATCH_SIZE)
        const { error } = await supabase
          .from('schwab_transactions')
          .insert(batch)

        if (error) {
          insertErrors.push(error.message)
        } else {
          insertedCount += batch.length
        }
        setProgress(`Inserting transactions... ${insertedCount}/${newRows.length}`)
      }

      // Step 4: Run grouping function
      setProgress('Grouping into trades...')
      const { data: groupResult, error: groupError } = await supabase
        .rpc('group_schwab_transactions_into_trades', { p_user_id: user.id })

      if (groupError) {
        insertErrors.push(`Grouping error: ${groupError.message}`)
      }

      // Step 5: Run reconciliation
      setProgress('Reconciling trades...')
      const { error: reconError } = await supabase
        .rpc('reconcile_trades_with_positions', { p_user_id: user.id })

      if (reconError) {
        insertErrors.push(`Reconciliation error: ${reconError.message}`)
      }

      // Step 6: Post-process STP orders — set stop_loss on trades
      setProgress('Processing stop loss data...')
      await processStopLosses(user.id, selectedAccount, parsed.executions)

      // Build result summary
      const gResult = groupResult || {}
      const details = [
        `${insertedCount} transactions inserted`,
        skippedCount > 0 ? `${skippedCount} duplicates skipped` : null,
        gResult.trades_created != null ? `${gResult.trades_created} trades created` : null,
        gResult.executions_created != null ? `${gResult.executions_created} executions created` : null,
        gResult.skipped != null && gResult.skipped > 0 ? `${gResult.skipped} executions already existed` : null,
      ].filter(Boolean)

      const importResult = {
        type: insertErrors.length > 0 ? 'warning' : 'success',
        message: insertErrors.length > 0
          ? `Import completed with ${insertErrors.length} warning(s)`
          : 'Import completed successfully',
        details: details.join(' · '),
        errors: insertErrors,
      }
      setResult(importResult)

      // Save import history
      await saveImportHistory(
        insertedCount,
        skippedCount,
        gResult.trades_created || 0,
        gResult.executions_created || 0
      )

      // Reset form for next upload (keep result banner visible)
      setFile(null)
      setParsed(null)
      setSelectedAccount('')
      setOverlapWarning(null)
      setDuplicateImportWarning(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (err) {
      setResult({ type: 'error', message: `Import failed: ${err.message}` })
    } finally {
      setImporting(false)
      setProgress('')
    }
  }

  // ── Save import history record ─────────────────────────────────────────────
  const saveImportHistory = async (insertedCount, skippedCount, tradesCreated, executionsCreated) => {
    if (!user || !file || !selectedAccount) return

    const nickname = nicknameSettings[selectedAccount]?.nickname || null
    const dateRange = parsed?.summary?.dateRange

    await supabase.from('import_history').insert({
      user_id: user.id,
      filename: file.name,
      account_number: selectedAccount,
      account_nickname: nickname,
      transactions_inserted: insertedCount,
      trades_created: tradesCreated,
      executions_created: executionsCreated,
      duplicates_skipped: skippedCount,
      date_range_start: dateRange?.from?.split('T')[0] || null,
      date_range_end: dateRange?.to?.split('T')[0] || null,
    })

    // Refresh history list
    await loadHistory()
  }

  // ── Reset ────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setFile(null)
    setParsed(null)
    setSelectedAccount('')
    setResult(null)
    setOverlapWarning(null)
    setDuplicateImportWarning(null)
    setProgress('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-black uppercase tracking-wide text-ink">Import Historical Trades</h1>
        <p className="text-sm text-inkMuted font-mono mt-0.5">Upload Schwab thinkorswim account statement CSV files</p>
      </div>

      {/* Result banner */}
      {result && (
        <div className={`flex items-start gap-3 px-4 py-3 rounded-lg text-sm font-semibold border ${
          result.type === 'success' ? 'bg-forest/10 border-forest/30 text-forest' :
          result.type === 'warning' ? 'bg-[#c4953a]/10 border-[#c4953a]/30 text-ink' :
          'bg-ember/10 border-ember/30 text-ember'
        }`}>
          {result.type === 'success' && <CheckCircle size={16} className="mt-0.5 shrink-0" />}
          {result.type === 'warning' && <AlertTriangle size={16} className="mt-0.5 shrink-0" />}
          {result.type === 'error' && <XCircle size={16} className="mt-0.5 shrink-0" />}
          <div className="flex-1">
            <div>{result.message}</div>
            {result.details && <div className="font-mono text-xs mt-1 opacity-80">{result.details}</div>}
            {result.errors?.length > 0 && (
              <div className="mt-2 text-xs font-mono opacity-70">
                {result.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
            {result.type === 'success' && (
              <Link
                to="/positions/history"
                className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-forest hover:underline"
              >
                View Trade Log &rarr;
              </Link>
            )}
          </div>
          <button onClick={() => setResult(null)} className="ml-auto opacity-60 hover:opacity-100 font-mono">&#10005;</button>
        </div>
      )}

      {/* Import History */}
      <div className="card">
        <h2 className="text-xs font-black uppercase tracking-wide text-inkMuted mb-3">Import History</h2>

        {historyLoading ? (
          <div className="font-mono text-xs text-inkMuted animate-pulse py-2">Loading...</div>
        ) : importHistory.length === 0 ? (
          <div className="font-mono text-xs text-inkMuted py-2">No imports yet</div>
        ) : (
          <div className="border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-canvas2 border-b border-border">
                  <th className="text-left px-3 py-2 font-semibold text-inkMuted uppercase tracking-wide">Filename</th>
                  <th className="text-left px-3 py-2 font-semibold text-inkMuted uppercase tracking-wide">Account</th>
                  <th className="text-left px-3 py-2 font-semibold text-inkMuted uppercase tracking-wide">Trades</th>
                  <th className="text-left px-3 py-2 font-semibold text-inkMuted uppercase tracking-wide">Date Range</th>
                  <th className="text-left px-3 py-2 font-semibold text-inkMuted uppercase tracking-wide">Imported</th>
                </tr>
              </thead>
              <tbody>
                {importHistory.map((h, i) => (
                  <tr key={h.id} className={`border-b border-border last:border-0 ${i % 2 === 0 ? '' : 'bg-canvas2/30'}`}>
                    <td className="px-3 py-2 font-mono font-semibold text-ink truncate max-w-[200px]">{h.filename}</td>
                    <td className="px-3 py-2 font-mono text-inkMuted whitespace-nowrap">{h.account_nickname || h.account_number}</td>
                    <td className="px-3 py-2 font-mono text-ink whitespace-nowrap">
                      {h.transactions_inserted} inserted{h.duplicates_skipped > 0 ? `, ${h.duplicates_skipped} skipped` : ''}
                    </td>
                    <td className="px-3 py-2 font-mono text-inkMuted whitespace-nowrap">
                      {h.date_range_start && h.date_range_end
                        ? `${fmtDate(h.date_range_start)} — ${fmtDate(h.date_range_end)}`
                        : '—'}
                    </td>
                    <td className="px-3 py-2 font-mono text-inkMuted whitespace-nowrap">{fmtRelativeTime(h.imported_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Step 1: Upload */}
      <div className="card">
        <h2 className="text-xs font-black uppercase tracking-wide text-inkMuted mb-3">Step 1 &mdash; Upload CSV</h2>

        {!file ? (
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-border rounded-lg p-10 text-center cursor-pointer hover:border-inkMuted transition-colors"
          >
            <Upload size={28} className="mx-auto text-inkMuted mb-3" />
            <div className="text-sm font-semibold text-inkMuted">Drag CSV file here or click to browse</div>
            <div className="text-xs font-mono text-border mt-1">Schwab thinkorswim Account Statement (.csv)</div>
          </div>
        ) : (
          <div className="flex items-center gap-3 px-4 py-3 bg-canvas2 rounded-lg border border-border">
            <FileText size={18} className="text-inkMuted shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ink truncate">{file.name}</div>
              <div className="text-xs font-mono text-inkMuted">{fmtFileSize(file.size)}</div>
            </div>
            <button onClick={handleReset} className="text-xs font-semibold text-inkMuted hover:text-ink">Change</button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(e) => handleFile(e.target.files[0])}
        />
      </div>

      {/* Step 2: Account Mapping */}
      {parsed && (
        <div className="card">
          <h2 className="text-xs font-black uppercase tracking-wide text-inkMuted mb-3">Step 2 &mdash; Map Account</h2>

          {parsed.accountName && (
            <div className="text-sm text-ink mb-2">
              CSV account: <span className="font-semibold">{parsed.accountName}</span>
            </div>
          )}

          <select
            value={selectedAccount}
            onChange={(e) => {
              setSelectedAccount(e.target.value)
              // Check overlap when account changes
              if (e.target.value && parsed) {
                setTimeout(() => checkOverlap(), 0)
              }
              // Check for duplicate import
              if (e.target.value && file) {
                checkDuplicateImport(file.name, e.target.value)
              }
            }}
            className="w-full max-w-md px-3 py-2 rounded-lg border border-border bg-canvas2 text-ink text-sm font-mono focus:outline-none focus:ring-1 focus:ring-forest"
          >
            <option value="">Select account...</option>
            {accountOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {duplicateImportWarning && (
            <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-[#c4953a]/10 border border-[#c4953a]/30 rounded-lg text-xs font-mono text-[#c4953a]">
              <AlertTriangle size={13} className="shrink-0" />
              {duplicateImportWarning}
            </div>
          )}

          {overlapWarning && (
            <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-[#c4953a]/10 border border-[#c4953a]/30 rounded-lg text-xs font-mono text-[#c4953a]">
              <AlertTriangle size={13} className="shrink-0" />
              {overlapWarning}
            </div>
          )}
        </div>
      )}

      {/* Step 3: Preview */}
      {parsed && parsed.executions.length > 0 && (
        <div className="card">
          <h2 className="text-xs font-black uppercase tracking-wide text-inkMuted mb-3">Step 3 &mdash; Preview</h2>

          {/* Summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="px-3 py-2 bg-canvas2 rounded-lg border border-border">
              <div className="text-xs font-mono text-inkMuted">Total Executions</div>
              <div className="text-lg font-black text-ink">{parsed.summary.total}</div>
            </div>
            <div className="px-3 py-2 bg-canvas2 rounded-lg border border-border">
              <div className="text-xs font-mono text-inkMuted">Date Range</div>
              <div className="text-xs font-semibold text-ink mt-0.5">
                {parsed.summary.dateRange
                  ? `${fmtDate(parsed.summary.dateRange.from)} — ${fmtDate(parsed.summary.dateRange.to)}`
                  : '—'}
              </div>
            </div>
            <div className="px-3 py-2 bg-canvas2 rounded-lg border border-border">
              <div className="text-xs font-mono text-inkMuted">Breakdown</div>
              <div className="text-xs font-mono text-ink mt-0.5">
                {[
                  parsed.summary.equities > 0 ? `${parsed.summary.equities} equity` : null,
                  parsed.summary.etfs > 0 ? `${parsed.summary.etfs} ETF` : null,
                  parsed.summary.options > 0 ? `${parsed.summary.options} option` : null,
                  parsed.summary.futures > 0 ? `${parsed.summary.futures} future` : null,
                ].filter(Boolean).join(', ') || '—'}
              </div>
            </div>
            <div className="px-3 py-2 bg-canvas2 rounded-lg border border-border">
              <div className="text-xs font-mono text-inkMuted">Stop Exits</div>
              <div className="text-lg font-black text-ink">{parsed.summary.stopTriggered}</div>
            </div>
          </div>

          {/* Preview table */}
          <div className="border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-canvas2 border-b border-border">
                  <th className="text-left px-3 py-2 font-semibold text-inkMuted uppercase tracking-wide">Date</th>
                  <th className="text-left px-3 py-2 font-semibold text-inkMuted uppercase tracking-wide">Time</th>
                  <th className="text-left px-3 py-2 font-semibold text-inkMuted uppercase tracking-wide">Side</th>
                  <th className="text-right px-3 py-2 font-semibold text-inkMuted uppercase tracking-wide">Qty</th>
                  <th className="text-left px-3 py-2 font-semibold text-inkMuted uppercase tracking-wide">Symbol</th>
                  <th className="text-left px-3 py-2 font-semibold text-inkMuted uppercase tracking-wide">Type</th>
                  <th className="text-right px-3 py-2 font-semibold text-inkMuted uppercase tracking-wide">Price</th>
                  <th className="text-left px-3 py-2 font-semibold text-inkMuted uppercase tracking-wide">Order</th>
                  <th className="text-left px-3 py-2 font-semibold text-inkMuted uppercase tracking-wide">Effect</th>
                </tr>
              </thead>
              <tbody>
                {parsed.executions.slice(0, 20).map((exec, i) => {
                  const isBuy = exec.sideRaw === 'BUY'
                  const dateParts = exec.tradeDate?.split('T') || ['—', '—']
                  return (
                    <tr key={i} className={`border-b border-border last:border-0 ${i % 2 === 0 ? '' : 'bg-canvas2/30'}`}>
                      <td className="px-3 py-2 font-mono text-ink whitespace-nowrap">{dateParts[0]}</td>
                      <td className="px-3 py-2 font-mono text-inkMuted whitespace-nowrap">{dateParts[1]}</td>
                      <td className={`px-3 py-2 font-semibold ${isBuy ? 'text-forest' : 'text-ember'}`}>
                        {exec.sideRaw}
                      </td>
                      <td className="px-3 py-2 font-mono text-ink text-right">{exec.qty}</td>
                      <td className="px-3 py-2 font-mono text-ink">{exec.rawSymbol}</td>
                      <td className="px-3 py-2 text-inkMuted">{exec.type}</td>
                      <td className="px-3 py-2 font-mono text-ink text-right">${exec.price.toFixed(2)}</td>
                      <td className="px-3 py-2">
                        {exec.orderType === 'STP' ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-ember/10 text-ember border border-ember/30">
                            STOP
                          </span>
                        ) : (
                          <span className="text-inkMuted">{exec.orderType}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-inkMuted font-mono text-[10px]">{exec.posEffect}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {parsed.executions.length > 20 && (
              <div className="px-3 py-2 text-xs font-mono text-inkMuted text-center bg-canvas2 border-t border-border">
                Showing 20 of {parsed.executions.length} executions
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 4: Import */}
      {parsed && parsed.executions.length > 0 && selectedAccount && (
        <div className="card">
          <h2 className="text-xs font-black uppercase tracking-wide text-inkMuted mb-3">Step 4 &mdash; Import</h2>

          {importing ? (
            <div className="flex items-center gap-3 py-2">
              <Loader2 size={16} className="animate-spin text-forest" />
              <span className="text-sm font-mono text-inkMuted">{progress}</span>
            </div>
          ) : (
            <button
              onClick={handleImport}
              disabled={importing || result?.type === 'success'}
              className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: 'rgb(var(--color-forest))' }}
            >
              Import {parsed.executions.length} Trades
            </button>
          )}
        </div>
      )}

      {/* Empty state */}
      {parsed && parsed.executions.length === 0 && (
        <div className="card text-center py-8">
          <div className="text-sm text-inkMuted font-mono">
            No trade executions found in this CSV file.
          </div>
          <div className="text-xs text-border font-mono mt-1">
            Make sure the file contains an "Account Trade History" section.
          </div>
        </div>
      )}
    </div>
  )
}

// ── Post-process: set stop_loss on trades with STP exits ─────────────────────

async function processStopLosses(userId, accountNumber, executions) {
  // Find STP executions that were CLOSING (exits)
  const stpExits = executions.filter(e =>
    e.orderType === 'STP' && e.positionEffect === 'CLOSING'
  )

  if (stpExits.length === 0) return

  for (const stpExec of stpExits) {
    const { data: trades } = await supabase
      .from('trades')
      .select('id, avg_entry_price, quantity_total, asset_type, net_pnl, initial_stop')
      .eq('user_id', userId)
      .eq('account_number', accountNumber)
      .eq('symbol', stpExec.symbol)
      .is('stop_loss', null)
      .order('created_at', { ascending: false })
      .limit(1)

    if (!trades || trades.length === 0) continue

    const trade = trades[0]
    const multiplier = trade.asset_type === 'option' ? 100 : 1
    // Use initial_stop for risk if already set, otherwise this stop IS the initial
    const riskStop = trade.initial_stop || stpExec.price
    const riskAmount = Math.abs(trade.avg_entry_price - riskStop) * trade.quantity_total * multiplier
    const rMultiple = trade.net_pnl != null && riskAmount > 0
      ? trade.net_pnl / riskAmount
      : null

    const updates = {
      stop_loss: stpExec.price,
      risk_amount: riskAmount,
      r_multiple: rMultiple,
    }
    // Set initial_stop if not already set
    if (!trade.initial_stop) {
      updates.initial_stop = stpExec.price
    }

    await supabase
      .from('trades')
      .update(updates)
      .eq('id', trade.id)
  }
}
